import _ from 'lodash';

/**
 * Return some interesting data from a route
 * @param google Google instance
 * @param response It's the response of Google Directions Service.
 * @returns {
 *   totalDistance: { text, value },
 *   totalDuration: { text, value },
 *   midpoint: { lat(), lng() },
 *   startLocation: { lat(), lng() },
 *   endLocation: { lat(), lng() },
 * }
 */
export const getDataFromRoute = (google, response) => {
  if (_.isNil(response)) {
    throw new Error('GetMiddlePointByRoute method needs a parameter');
  }

  // Check routes
  // NOTE: There will only be one route unless the DirectionsRequest
  // was made with provideRouteAlternatives set to true
  const thereIsNotRoutes = _.isNil(response.routes)
    || (_.isArray(response.routes) && response.routes.length === 0);

  if (thereIsNotRoutes) {
    return undefined;
  }

  // Check legs
  // DEV NOTE: We always will check the first route
  // NOTE: There will be one leg for each stopover waypoint or
  // destination specified.
  const thereIsNotLegs = _.isNil(response.routes[0].legs)
    || (_.isArray(response.routes[0].legs) && response.routes[0].legs.length === 0);

  if (thereIsNotLegs) {
    return undefined;
  }

  // DEV NOTE: We always will check the first legs. This
  // function is not intended for routes with waypoints.
  const leg = response.routes[0].legs[0];

  // Check steps
  // NOTE: An array of DirectionsSteps, each of which
  // contains information about the individual steps in this leg.
  const thereIsNotSteps = _.isNil(leg.steps)
    || (_.isArray(leg.steps) && leg.steps.length === 0);

  if (thereIsNotSteps) {
    return undefined;
  }

  let STEP = null;
  let countDistanceTraveled = 0;
  const TOTAL_DISTANCE = leg.distance.value;
  const MIDDLE_DISTANCE = TOTAL_DISTANCE / 2;

  // Now, we will iterate between all steps and find
  // where the middle point is.
  for (let i = 0; i < leg.steps.length; i += 1) {
    const step = leg.steps[i];
    const traveledInThisStep = countDistanceTraveled + step.distance.value;

    if (countDistanceTraveled <= MIDDLE_DISTANCE && MIDDLE_DISTANCE < traveledInThisStep) {
      STEP = step;
      break;
    }

    countDistanceTraveled += step.distance.value;
  }

  if (_.isNil(STEP)) {
    return undefined;
  }

  const startLocationLat = STEP.start_location.lat();
  const startLocationLng = STEP.start_location.lng();
  const endLocationLat = STEP.end_location.lat();
  const endLocationLng = STEP.end_location.lng();

  let middlePointLat = null;
  if (startLocationLat > endLocationLat) {
    middlePointLat = (startLocationLat - ((startLocationLat - endLocationLat) / 2));
  } else {
    middlePointLat = (startLocationLat + -((startLocationLat - endLocationLat) / 2));
  }

  let middlePointLng = null;
  if (startLocationLng > endLocationLng) {
    middlePointLng = (startLocationLng - ((startLocationLng - endLocationLng) / 2));
  } else {
    middlePointLng = (startLocationLng + -((startLocationLng - endLocationLng) / 2));
  }

  return {
    // totalDistance = { text, value }
    totalDistance: leg.distance,
    // totalDuration = { text, value }
    totalDuration: leg.duration,
    // midpoint = { lat(), lng() }
    midpoint: new google.maps.LatLng({
      lat: middlePointLat,
      lng: middlePointLng,
    }),
    // startLocation = { lat(), lng() }
    startLocation: leg.start_location,
    // endLocation = { lat(), lng() }
    endLocation: leg.end_location,
  };
};

export default {
  getDataFromRoute,
};
